import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getSuppliers } from '../../clients/supplier_client';
import { getManufacturerSelectList } from '../../clients/manufacturer_client';
import SupplierModal from '../../components/Supplier/SupplierModal';
import InfiniteScroll from 'react-infinite-scroller';
import { Spinner } from 'react-bootstrap';

import GenericSearchFilter from '../../components/Generic/GenericSearchFilter';
import SupplierTable from '../../components/Supplier/SupplierTable';
import { showToast } from '../../config_and_helpers/helpers';
import {
  logError,
  logInfiniteScrollDebug,
  logListSearchEvent,
  logListSearchError,
} from '../../config_and_helpers/logger';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  resetPaginationForPartialSearch,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';

export const SupplierContext = React.createContext();

const SUPPLIERS_PAGE_CONTEXT = 'suppliersPage';

/**
 * Writes supplier-page trace events to the shared in-memory log buffer (and dev console via logger).
 * @param {string} label - Event name for the trace.
 * @param {Object} [data] - Structured metadata for the event.
 */
const logSuppliersDebug = (label, data = {}) => {
  const payload = data || {};
  logInfiniteScrollDebug(SUPPLIERS_PAGE_CONTEXT, label, payload);
};

/**
 * SuppliersPage is a component that handles state, context, and UI logic for displaying a list of suppliers.
 * It supports debounced text search, manufacturer-based filtering, and paginated infinite scrolling.
 * Server-side filtering is applied based on search input and manufacturer ID.
 *
 * @returns {JSX.Element} - JSX element containing the supplier search bar, filter UI, and infinite scroll list
 */
const SuppliersPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  const isMountedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null); // Used to cancel previous requests
  const hasInitialFetchRef = useRef(false); // Track if initial fetch has been done for this mount
  const hasMoreRef = useRef(true);

  const [suppliers, setSuppliers] = useState([]);
  const [manufacturer, setManufacturer] = useState('');
  const [manufacturerSelectList, setManufacturerSelectList] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false); // Search-specific loading state
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  // Error state for UI (currently not used but kept for future use)
  // const [hasError, setHasError] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchSuppliers = useCallback(
    (
      {
        searchValue = '',
        nextPage = null,
        manufacturerId = '',
        sortByValue = 'name',
        sortDirectionValue = 'asc',
      } = {},
      newSearch = false
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const requestOptions = {
        searchInput: searchValue,
        manufacturerId: manufacturerId,
        nextPage: nextPage,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
      };

      logSuppliersDebug('fetchSuppliers:start', {
        params: {
          searchValue,
          nextPage,
          manufacturerId,
          newSearch,
        },
        refs: {
          hasInitialFetch: hasInitialFetchRef.current,
          isLoadingRef: isLoadingRef.current,
        },
        stateSnapshot: {
          suppliersCount: suppliers.length,
          nextPageUrl,
          hasMore,
          isLoading,
          isSearching,
        },
      });

      // Prevent double fetching on initial load
      if (!nextPage && !hasInitialFetchRef.current) {
        hasInitialFetchRef.current = true;
        logSuppliersDebug('fetchSuppliers:mark-initial-fetch', {
          nextPage,
          hasInitialFetch: hasInitialFetchRef.current,
        });
      }

      // Cancel previous request only when starting a replacement fetch
      if (currentRequestRef.current) {
        logSuppliersDebug('fetchSuppliers:cancel-previous-request', {
          hadPreviousRequest: true,
        });
        abortListSearchController(currentRequestRef);
      }

      // Set loading state
      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        // Clear existing suppliers for new search
        setSuppliers([]);
      }
      isLoadingRef.current = true;
      logSuppliersDebug('fetchSuppliers:set-loading', {
        newSearch,
        isLoadingRef: isLoadingRef.current,
        isLoading: true,
        isSearching: newSearch,
      });

      // Create a cancelable promise
      const controller = new AbortController();
      currentRequestRef.current = controller;

      logSuppliersDebug('fetchSuppliers:before-getSuppliers', {
        requestOptions,
      });

      logListSearchEvent('suppliers', 'request', {
        trigger,
        params: requestOptions,
      });

      getSuppliers(token, setSuppliers, {
        ...requestOptions,
        signal: controller.signal,
      })
        .then(response => {
          if (!isMountedRef.current) {
            logSuppliersDebug('fetchSuppliers:response-ignored-unmounted', {
              responseMeta: {
                hasSuccessField: !!response?.success,
              },
            });
            return;
          }
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          logSuppliersDebug('fetchSuppliers:response', {
            responseMeta: {
              success: response?.success,
              reachedEnd: response?.reachedEnd,
              nextPage: response?.nextPage,
            },
          });

          const responseMeta = {
            success: response?.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (response?.success) {
            setNextPageUrl(response.reachedEnd ? null : response.nextPage);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
            logSuppliersDebug('fetchSuppliers:update-pagination-state', {
              reachedEnd: !!response.reachedEnd,
              nextPage: response.reachedEnd ? null : response.nextPage,
              hasMoreAfterUpdate: !response.reachedEnd,
            });
          } else {
            logSuppliersDebug('fetchSuppliers:non-success-response', {
              response,
            });
            showToast(
              'An unexpected error occurred. Please try again',
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            'suppliers',
            response?.success ? 'success' : 'failure',
            {
              trigger,
              params: requestOptions,
              responseMeta,
            }
          );

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          logSuppliersDebug('fetchSuppliers:finish-success', {
            isLoadingRef: isLoadingRef.current,
            isLoading: false,
            isSearching: false,
            hasMore,
            nextPageUrl,
          });
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            logSuppliersDebug('fetchSuppliers:abort-error', {
              name: error.name,
              message: error.message,
            });
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;

          logError(
            'suppliersInfiniteScroll',
            'fetchSuppliers error',
            error
          );
          logSuppliersDebug('fetchSuppliers:error', {
            name: error.name,
            message: error.message,
          });

          logListSearchError('suppliers', trigger, error, {
            params: requestOptions,
          });

          // Show error toast
          showToast(
            'Failed to load suppliers. Please try again.',
            'error',
            'top-center',
            3000
          );

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          logSuppliersDebug('fetchSuppliers:finish-error', {
            isLoadingRef: isLoadingRef.current,
            isLoading: false,
            isSearching: false,
            hasMore,
            nextPageUrl,
          });
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Remove token dependency to prevent infinite loop
  );

  useEffect(() => {
    isMountedRef.current = true;
    logSuppliersDebug('mount', {
      hasInitialFetch: hasInitialFetchRef.current,
    });

    // Fetch suppliers on mount if not already fetched for this mount
    if (!hasInitialFetchRef.current) {
      hasInitialFetchRef.current = true;
      setIsLoading(true);
      isLoadingRef.current = true;
      logSuppliersDebug('mount:initial-fetch', {
        isLoadingRef: isLoadingRef.current,
      });
      fetchSuppliers({
        searchValue: '',
        nextPage: null,
        manufacturerId: '',
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      });
    }

    return () => {
      isMountedRef.current = false;
      // Reset the ref when component unmounts so it fetches again on next mount
      hasInitialFetchRef.current = false;
       logSuppliersDebug('unmount', {
         hasInitialFetch: hasInitialFetchRef.current,
       });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array

  // Fetch manufacturer select list on component mount
  useEffect(() => {
    getManufacturerSelectList(token, setManufacturerSelectList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Cleanup effect for debounce timeout and requests
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        logSuppliersDebug('cleanup:cleared-debounce-timeout');
      }
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
        logSuppliersDebug('cleanup:cancel-current-request');
      }
    };
  }, []);

  /**
   * Smart debounced search input handler with immediate UI feedback
   */
  const debouncedFetch = useCallback(
    (val, manufacturerId, sortByValue, sortDirectionValue) => {
      logSuppliersDebug('debouncedFetch:called', {
        valueLength: val.length,
        manufacturerId,
      });
      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        logSuppliersDebug('debouncedFetch:cleared-previous-timeout');
      }

      // Immediate UI feedback: clear results and show searching state
      if (val.length >= 2) {
        setSuppliers([]);
        setIsSearching(true);
        logSuppliersDebug('debouncedFetch:set-searching-state', {
          searchValue: val,
        });
      }

      // Only search if we have 2+ characters or empty string
      if (isRunnableListSearchQuery(val)) {
        debounceTimeoutRef.current = setTimeout(() => {
          logSuppliersDebug('debouncedFetch:trigger-fetchSuppliers', {
            searchValue: val,
            manufacturerId,
          });
          fetchSuppliers(
            {
              searchValue: val,
              nextPage: null,
              manufacturerId,
              sortByValue,
              sortDirectionValue,
            },
            true
          ); // newSearch = true for search operations
        }, 300); // Reduced from 500ms to 300ms for better responsiveness
      } else if (isPartialListSearchQuery(val)) {
        setSuppliers([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
        logSuppliersDebug('debouncedFetch:single-char-reset', {
          searchValue: val,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Remove fetchSuppliers dependency to prevent infinite loop
  );

  const handleSearchInput = val => {
    logSuppliersDebug('handleSearchInput', {
      previousSearchInput: searchInput,
      nextSearchInput: val,
    });
    setSearchInput(val);
    debouncedFetch(val, manufacturer, sortBy, sortDirection);
  };

  const handleManufacturerChange = val => {
    logSuppliersDebug('handleManufacturerChange', {
      previousManufacturer: manufacturer,
      nextManufacturer: val,
      currentSearchInput: searchInput,
    });
    setManufacturer(val);
    fetchSuppliers(
      {
        searchValue: searchInput,
        nextPage: null,
        manufacturerId: val,
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    ); // newSearch = true for filter changes
  };

  const handleSortByChange = (value, direction = 'asc') => {
    setSortBy(value);
    setSortDirection(direction);
    fetchSuppliers(
      {
        searchValue: searchInput,
        nextPage: null,
        manufacturerId: manufacturer,
        sortByValue: value,
        sortDirectionValue: direction,
      },
      true
    );
  };

  const resetSearchValue = () => {
    logSuppliersDebug('resetSearchValue', {
      previousSearchInput: searchInput,
    });
    setSearchInput('');
  };

  return (
    <GlassWorkspaceShell theme="rose" wide>
    <SupplierContext.Provider value={{ suppliers, setSuppliers }}>
      <div className="glass-toolbar">
      <GenericSearchFilter
        entityType="manufacturers"
        placeHolderLabel="Manufacturer"
        searchInput={searchInput}
        onSearchInput={handleSearchInput}
        onEntityChange={handleManufacturerChange}
        entity={manufacturer}
        supplierSelectList={manufacturerSelectList}
        ModalComponent={
          isViewer ? null : (
          <SupplierModal
            onSuccessfulSubmit={
              () =>
                fetchSuppliers(
                  {
                    searchValue: searchInput,
                    manufacturerId: manufacturer,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  true
                ) // newSearch = true for refresh after modal submit
            }
            clearSearchValue={resetSearchValue}
          />
          )
        }
      />
      </div>

      {(isLoading && suppliers.length === 0) || isSearching ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && searchInput.length >= 2 && (
            <div className="mt-2 text-muted">Searching suppliers...</div>
          )}
        </div>
      ) : (
        <>
          {/* No results state */}
          {!isLoading && !isSearching && suppliers.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <span
                  role="img"
                  aria-label="no results"
                  style={{ fontSize: '3rem' }}
                >
                  🏢
                </span>
              </div>
              <h5 className="text-muted mb-2">No suppliers found</h5>
              <p className="text-muted mb-3">
                {searchInput.length >= 2
                  ? `No suppliers match your search for "${searchInput}"`
                  : 'No suppliers available'}
              </p>
              {searchInput.length >= 2 && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setSearchInput('');
                    fetchSuppliers(
                      {
                        searchValue: '',
                        nextPage: null,
                        manufacturerId: manufacturer,
                        sortByValue: sortBy,
                        sortDirectionValue: sortDirection,
                      },
                      true
                    );
                  }}
                >
                  <span role="img" aria-label="clear search">
                    🗑️
                  </span>{' '}
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <InfiniteScroll
              pageStart={0}
              loadMore={page => {
                const snapshot = {
                  page,
                  hasMore,
                  nextPageUrl,
                  isLoadingRef: isLoadingRef.current,
                  hasInitialFetch: hasInitialFetchRef.current,
                  isLoading,
                  isSearching,
                  suppliersCount: suppliers.length,
                };
                logSuppliersDebug('InfiniteScroll:loadMore:called', snapshot);

                if (
                  isLoadingRef.current ||
                  !hasInitialFetchRef.current ||
                  !nextPageUrl ||
                  isPartialListSearchQuery(searchInput)
                ) {
                  logSuppliersDebug('InfiniteScroll:loadMore:skipped', {
                    reason: isLoadingRef.current
                      ? 'isLoadingRef-currently-true'
                      : !hasInitialFetchRef.current
                        ? 'initial-fetch-not-complete'
                        : !nextPageUrl
                          ? 'no-next-page-url'
                          : 'partial-search-query',
                    snapshot,
                  });
                  return;
                }

                const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
                  search: searchInput,
                  manufacturer_id: manufacturer,
                  sort_by: sortBy,
                  sort_direction: sortDirection,
                });
                if (mismatch === null) {
                  logListSearchError(
                    'suppliers',
                    'next-page-url-stale-check',
                    new Error('Failed to parse nextPageUrl'),
                    { nextPageUrl, searchInput, manufacturer, sortBy, sortDirection }
                  );
                  setHasMore(false);
                  hasMoreRef.current = false;
                  setNextPageUrl(null);
                  return;
                }
                if (mismatch.mismatched) {
                  logListSearchEvent('suppliers', 'next-page-url-stale-mismatch', {
                    nextPageUrl,
                    expected: mismatch.expected,
                    actual: mismatch.actual,
                  });
                  setHasMore(false);
                  hasMoreRef.current = false;
                  setNextPageUrl(null);
                  return;
                }

                const request = {
                  searchValue: searchInput,
                  nextPage: nextPageUrl,
                  manufacturerId: manufacturer,
                  sortByValue: sortBy,
                  sortDirectionValue: sortDirection,
                };

                logSuppliersDebug(
                  'InfiniteScroll:loadMore:trigger-fetchSuppliers',
                  {
                    page,
                    request,
                  }
                );

                fetchSuppliers(request, false); // newSearch = false for pagination
              }}
              hasMore={hasMore}
              loader={
                <div key="supplier-loader" className="text-center py-2">
                  <Spinner animation="border" size="sm" role="status" />
                  <div className="mt-1">
                    <small className="text-muted">
                      Loading more suppliers...
                    </small>
                  </div>
                </div>
              }
              useWindow={true}
              threshold={INFINITE_SCROLL_THRESHOLD_PX}
            >
              <div className="glass-table-wrap">
              <SupplierTable
                supplierList={suppliers}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSortByChange}
                handleChange={
                  () =>
                    fetchSuppliers(
                      {
                        searchValue: searchInput,
                        manufacturerId: manufacturer,
                        sortByValue: sortBy,
                        sortDirectionValue: sortDirection,
                      },
                      true
                    ) // newSearch = true for refresh after change
                }
                clearSearchValue={resetSearchValue}
              />
              </div>
            </InfiniteScroll>
          )}
        </>
      )}
    </SupplierContext.Provider>
    </GlassWorkspaceShell>
  );
};

export default SuppliersPage;
